import { OverlayModule } from '@angular/cdk/overlay';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { LevelBarComponent } from '@core/components/level-bar/level-bar.component';
import { fadeInAnimation } from '@core/utils/animations.util';
import { PinnedMessageService } from '../pinned-message.service';
import { ClickOutsideDirective } from '@core/directives/click-outside.directive';
import { UIService } from '@core/services/ui.service';
import { sleep } from '@core/utils/sleep.util';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-pinned-message-widget',
  imports: [
    SvgIconComponent,
    OverlayModule,
    LevelBarComponent,
    ClickOutsideDirective,
  ],
  templateUrl: './pinned-message-widget.component.html',
  styleUrl: './pinned-message-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  animations: [fadeInAnimation],
})
export class ChatPinnedMessageComponent {
  public readonly pinnedMessageService = inject(PinnedMessageService);
  public readonly uiService = inject(UIService);
  public readonly toastrService = inject(ToastrService);

  isShowPinnedMessage = signal<boolean>(false);
  isShowCountdown = signal<boolean>(false);

  public countDownCurrent = signal<number>(100);
  private countdownIntervalId: any = null;

  resizeEffect = afterRenderEffect(() => {
    const onResize = () => {
      this.destroyCountdown();
      this.resizeEffect.destroy();
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  });

  showPinnedMessageEffectDesktop = effect(() => {
    const { pinnedMessage, isAlwaysHidePinnedMessage } =
      this.pinnedMessageService;

    const isChatHiddenByDefault = this.uiService.breakpoints().max_xl2;

    if (
      pinnedMessage() &&
      !isAlwaysHidePinnedMessage() &&
      !isChatHiddenByDefault
    ) {
      this.isShowPinnedMessage.set(true);
      this.isShowCountdown.set(true);
      this.showPinnedMessageEffectMobile.destroy();
      this.showPinnedMessageEffectDesktop.destroy();
    }
  });

  showPinnedMessageByToastMobile = effect(() => {
    const { pinnedMessage, isAlwaysHidePinnedMessage } =
      this.pinnedMessageService;

    const isChatHiddenByDefault = this.uiService.breakpoints().max_xl2;

    if (
      pinnedMessage() &&
      !isAlwaysHidePinnedMessage() &&
      isChatHiddenByDefault &&
      !this.uiService.isShowChat()
    ) {
      this.toastrService.info('', pinnedMessage()!.message, {
        timeOut: 10000,
      });
      this.showPinnedMessageByToastMobile.destroy();
    }
  });

  showPinnedMessageEffectMobile = effect(async () => {
    const { pinnedMessage, isAlwaysHidePinnedMessage } =
      this.pinnedMessageService;

    const isChatHiddenByDefault = this.uiService.breakpoints().max_xl2;

    if (
      pinnedMessage() &&
      !isAlwaysHidePinnedMessage() &&
      isChatHiddenByDefault &&
      this.uiService.isShowChat()
    ) {
      await sleep(300);
      this.isShowPinnedMessage.set(true);
      this.isShowCountdown.set(true);

      this.showPinnedMessageEffectMobile.destroy();
      this.showPinnedMessageEffectDesktop.destroy();
    }
  });

  initIntervalEffect = afterRenderEffect(() => {
    if (this.isShowCountdown()) {
      const intervalId = setInterval(() => {
        this.countDownCurrent.update((prev) => prev - 5);
      }, 1000);
      this.countdownIntervalId = intervalId;
      this.initIntervalEffect.destroy();
    }
  });

  destroyIntervalEffect = afterRenderEffect(() => {
    const isReset = this.countDownCurrent() <= 0;
    const isActiveCountdown =
      this.countDownCurrent() < 100 && !this.isShowPinnedMessage();
    if (isReset || isActiveCountdown) {
      this.destroyCountdown();
    }
  });

  public setShowPinnedMessage(type: 'show' | 'hide' | 'toggle') {
    this.isShowPinnedMessage.update((prev) => {
      if (type === 'show') return true;
      if (type === 'hide') return false;
      return !prev;
    });
  }

  private destroyCountdown() {
    clearInterval(this.countdownIntervalId);
    this.isShowPinnedMessage.set(false);
    this.isShowCountdown.set(false);
    this.countDownCurrent.set(100);
    this.destroyIntervalEffect.destroy();
  }
}
